import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { 
  X, 
  Send, 
  Trash2, 
  Sparkles, 
  User,
  Loader2,
  Command,
  ChevronDown,
  Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useGlobalStore } from '@/core/store';
import { useAISidebar, ChatMessage } from './AISidebarContext';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';

// 快捷命令
const quickCommands = [
  { id: 'accounts', label: '查询账号', icon: '👤', prompt: '帮我查询账号数据' },
  { id: 'content', label: '生成内容', icon: '✏️', prompt: '帮我生成一篇小红书笔记' },
  { id: 'analytics', label: '数据分析', icon: '📊', prompt: '分析今日数据表现' },
  { id: 'competitor', label: '竞品分析', icon: '🔍', prompt: '分析竞品账号表现' },
];

export function AISidebar() {
  const { aiSidebarOpen, setAISidebarOpen } = useGlobalStore();
  const { messages, isLoading, sendMessage, clearHistory } = useAISidebar();
  const [input, setInput] = useState('');
  const [showQuickCommands, setShowQuickCommands] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // 自动滚动到底部
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  // 打开时聚焦输入框
  useEffect(() => {
    if (aiSidebarOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [aiSidebarOpen]);

  const handleSubmit = async () => {
    if (!input.trim() || isLoading) return;
    const message = input;
    setInput('');
    await sendMessage(message);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleQuickCommand = async (prompt: string) => {
    setShowQuickCommands(false);
    setInput('');
    await sendMessage(prompt);
  };

  if (!aiSidebarOpen) return null;

  return (
    <aside className="fixed right-0 top-0 z-40 h-screen w-[400px] bg-background border-l border-border flex flex-col animate-slide-in-right">
      {/* 头部 */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-border bg-gradient-to-r from-hermes-500/10 to-transparent">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg gradient-hermes flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold">AI 助手</h3>
            <p className="text-xs text-muted-foreground">
              <Command className="inline h-3 w-3" />K 快速唤起
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={clearHistory}
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setAISidebarOpen(false)}
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* 消息列表 */}
      <ScrollArea ref={scrollAreaRef} className="flex-1 p-4">
        <div className="space-y-4">
          {messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))}
          {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">思考中...</span>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* 快捷命令面板 */}
      {showQuickCommands && (
        <div className="border-t border-border bg-secondary/30 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Zap className="h-3 w-3" /> 快捷命令
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowQuickCommands(false)}
              className="h-6 w-6 p-0"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {quickCommands.map((cmd) => (
              <button
                key={cmd.id}
                onClick={() => handleQuickCommand(cmd.prompt)}
                className="flex items-center gap-2 p-2 rounded-lg bg-background hover:bg-accent transition-colors text-left"
              >
                <span>{cmd.icon}</span>
                <span className="text-sm">{cmd.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <Separator />

      {/* 输入区域 */}
      <div className="p-4">
        <div className="relative">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="输入消息，按 Enter 发送..."
            className={cn(
              'w-full min-h-[80px] max-h-[200px] p-3 pr-12 rounded-xl resize-none',
              'bg-secondary/50 border border-border',
              'focus:outline-none focus:ring-2 focus:ring-hermes-500/50 focus:border-hermes-500',
              'placeholder:text-muted-foreground text-sm'
            )}
            disabled={isLoading}
          />
          <div className="absolute right-2 bottom-2 flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowQuickCommands(!showQuickCommands)}
              className="h-8 w-8 text-muted-foreground hover:text-hermes-500"
            >
              <Zap className="h-4 w-4" />
            </Button>
            <Button
              variant="hermes"
              size="icon"
              onClick={handleSubmit}
              disabled={!input.trim() || isLoading}
              className="h-8 w-8"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Shift + Enter 换行 · Enter 发送
        </p>
      </div>
    </aside>
  );
}

// 消息气泡组件
function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex gap-3', isUser && 'flex-row-reverse')}>
      {/* 头像 */}
      <div
        className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center shrink-0',
          isUser ? 'bg-hermes-500' : 'gradient-hermes'
        )}
      >
        {isUser ? (
          <User className="h-4 w-4 text-white" />
        ) : (
          <Sparkles className="h-4 w-4 text-white" />
        )}
      </div>

      {/* 消息内容 */}
      <div
        className={cn(
          'flex-1 max-w-[85%]',
          isUser && 'flex flex-col items-end'
        )}
      >
        <div
          className={cn(
            'rounded-2xl px-4 py-2.5 text-sm',
            isUser
              ? 'bg-hermes-500 text-white rounded-tr-md'
              : 'bg-secondary rounded-tl-md'
          )}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap">{message.content}</p>
          ) : (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown
                components={{
                  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                  ul: ({ children }) => <ul className="mb-2 pl-4 list-disc">{children}</ul>,
                  ol: ({ children }) => <ol className="mb-2 pl-4 list-decimal">{children}</ol>,
                  li: ({ children }) => <li className="mb-1">{children}</li>,
                  code: ({ children }) => (
                    <code className="px-1.5 py-0.5 rounded bg-background/50 text-hermes-500 font-mono text-xs">
                      {children}
                    </code>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold text-hermes-500">{children}</strong>
                  ),
                }}
              >
                {message.content || '...'}
              </ReactMarkdown>
            </div>
          )}
          {message.isStreaming && (
            <span className="inline-block w-2 h-4 bg-current animate-pulse ml-1" />
          )}
        </div>
        <span className="text-[10px] text-muted-foreground mt-1 px-1">
          {formatTime(message.timestamp)}
        </span>
      </div>
    </div>
  );
}

function formatTime(date: Date): string {
  return new Intl.DateTimeFormat('zh-CN', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

